/**
 * @prettier
 */

import _values from 'lodash/values';
import ApiService from '../../../../../util/api';
import {
  extractWeddingNames,
  extractUserWeddingDetails,
  matchColorToCardVariation,
  parseWeddingDate,
  createSubstitutions,
} from './helpers';

export function getUserContext() {
  return ApiService.get('/website-nav/web-api/v1/user/get');
}

function creatInvitationsAccount() {
  return ApiService.post('/web-api/v1/invitation-account');
}

export function createAccount(accountDetails, weddingDetails, cardType) {
  const weddingNames = extractWeddingNames(weddingDetails);

  const date = weddingDetails && weddingDetails.date;
  const parsedDate = parseWeddingDate(date);

  const invalidEmail = 'Please make sure to enter a valid email address.';
  const invalidPassword = 'Please make your password is at least 8 characters.';
  const emptyWeddingDetails = 'Please make sure you enter your full names in the wedding details.';
  const invalidWeddingDate = 'Please make sure your wedding date is valid';
  const invalidWeddingDetails = 'Please make sure your wedding details are valid.';

  if (!accountDetails.email) {
    return Promise.resolve({ errors: { account: invalidEmail } });
  }

  if (!accountDetails.password || accountDetails.password.length < 8) {
    return Promise.resolve({ errors: { account: invalidPassword } });
  }

  if (!weddingNames) {
    return Promise.resolve({ errors: { weddingDetails: emptyWeddingDetails } });
  }

  if (date && date.length > 0 && !parsedDate) {
    return Promise.resolve({ errors: { weddingDetails: invalidWeddingDate } });
  }

  return ApiService.post('/web-api/v1/user/create', {
    email: accountDetails.email,
    password: accountDetails.password,
    business_unit_feature: cardType,
  })
    .then(response => {
      if (response.status === 'error') {
        return { errors: { account: response.message || invalidEmail } };
      }

      return {};
    })
    .then(result => {
      if (result.errors) {
        return result;
      }

      return ApiService.post('/web-api/v1/onboard/submit', {
        business_unit: 'INVITATIONS',
        answers: [],
        existing_account: true,
        wedding_details: {
          ...weddingNames,
          event_date: !weddingDetails.stillDeciding ? weddingDetails.date : null,
        },
      })
        .catch(() => {
          return { errors: { weddingDetails: invalidWeddingDetails } };
        })
        .then(onboardResult => {
          if (onboardResult.status === 'error' || onboardResult.errors) {
            return { errors: { weddingDetails: onboardResult.message || invalidWeddingDetails } };
          }

          return {};
        });
    })
    .then(result => {
      if (result.errors) return result;

      return getUserContext().then(userCtx => {
        if (!userCtx.has_invitation_account) {
          return creatInvitationsAccount().then(() => ({}));
        }

        return {};
      });
    });
}

export function getCardSuiteDetails(cardSuiteUUID, isAdminView = false) {
  return ApiService.post(`/web-api/v1/card-catalog/suite/details/${cardSuiteUUID}`, {
    show_unlisted: !!isAdminView,
  });
}

export function uploadPhoto(photo, crop) {
  if (!photo.valid) {
    return Promise.resolve();
  }

  const formData = new FormData();

  formData.append('attachment', photo.file);
  formData.append('size', Math.max(photo.size.width, photo.size.height));

  return fetch('/web-api/v1/image/temp-upload', {
    credentials: 'same-origin',
    method: 'POST',
    body: formData,
  })
    .then(response => response.json())
    .then(data => {
      return fetch('/web-api/v1/image/crop-save', {
        credentials: 'same-origin',
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify({
          bucketName: data.s3_bucket,
          s3key: data.s3_key,
          crop,
          contentType: data.content_type,
        }),
      });
    })
    .then(response => response.json())
    .then(json => json && json.data.uuid);
}

export function isSingleSampleLimitExhausted(cardType) {
  if (!cardType) {
    return Promise.resolve(false);
  }

  return ApiService.get(`/web-api/v1/card-single-sample/available/${cardType}`).then(
    available => !available
  );
}

export function submitSingleSampleForm(
  cardSuiteDetails,
  form,
  isGuest,
  userContext,
  fetchUserContext,
  photoUploadHandler,
  sampleNotCustomizable,
  nonEditableSingleSampleSku,
  isMagnet
) {
  const { type: cardType, uuid: suiteUUID } = cardSuiteDetails;

  const { variation } = form.customizations;
  const activeVariationUUID =
    (variation && variation.uuid) ||
    matchColorToCardVariation(cardSuiteDetails, form.customizations.color, isMagnet).uuid;

  let accountPromise;

  if (isGuest) {
    accountPromise = createAccount(form.account, form.weddingDetails, cardType).then(result => {
      if (result.errors) {
        return result;
      }

      return fetchUserContext().then(() => ({}));
    });
  } else if (!userContext.has_invitation_account) {
    // We manually create an invitations account since it's required to load the drafts & orders pages.
    accountPromise = creatInvitationsAccount().then(() => ({}));
  } else {
    accountPromise = Promise.resolve({});
  }

  return accountPromise
    .then(result => {
      if (
        !result.errors &&
        form &&
        form.customizations &&
        form.customizations.photo &&
        form.customizations.photo.valid &&
        form.customizations.pixelCrop
      ) {
        return photoUploadHandler(form.customizations.photo, form.customizations.pixelCrop).then(
          photoUUID => ({
            ...result,
            photoUUID,
          })
        );
      }

      return result;
    })
    .then(result => {
      if (result.errors) {
        return result;
      }

      const weddingNames = extractWeddingNames(form.weddingDetails);

      if (!weddingNames) {
        return { errors: { weddingDetails: 'Please make sure you enter your wedding details.' } };
      }

      const substitutions = sampleNotCustomizable
        ? {}
        : createSubstitutions(form && form.weddingDetails);

      const cardInfo = {
        // TODO once sku info added to PLP remove cardSuiteDetail sku fallback
        sku_object_id: nonEditableSingleSampleSku || cardSuiteDetails.single_sample_sku,
        variation_uuid: activeVariationUUID,
        suite_uuid: suiteUUID,
        wedding_details: sampleNotCustomizable
          ? {}
          : {
              ...weddingNames,
              event_date: !form.weddingDetails.stillDeciding ? form.weddingDetails.date : null,
              ...substitutions,
            },
      };

      const body = {
        ...cardInfo,
        card_type: cardType,
        photo_uuid: result.photoUUID,
        address_validation: {
          street_line_1: form.shipping.address,
          street_line_2: form.shipping.apt,
          city: form.shipping.city,
          state_province_region: form.shipping.state,
          postal_code: form.shipping.zipCode,
          country_code: 'USA',
        },
      };

      return ApiService.post('/web-api/v1/card-single-sample/order', body).then(data => {
        if (!data.addressValidated) {
          return { errors: { shipping: 'Please check that your shipping details are valid.' } };
        }
        if (!data.checkedOut) {
          return { errors: { shipping: 'There was a problem with your order.' } };
        }

        return { orderDetails: { id: data.orderNumber } };
      });
    })
    .then(result => {
      if (result.errors) {
        return result;
      }

      return getUserContext().then(data => ({
        orderDetails: { ...(result.orderDetails || {}), email: data.email },
      }));
    });
}

export function saveSingleSampleFormAsDraft(
  cardSuiteDetails,
  form,
  userContext,
  photoUploadHandler,
  overrideActiveVariationUUID,
  isMagnet
) {
  const { uuid: suiteUUID } = cardSuiteDetails;

  const { variation } = form.customizations;
  const activeVariationUUID =
    (variation && variation.uuid) ||
    overrideActiveVariationUUID ||
    matchColorToCardVariation(cardSuiteDetails, form.customizations.color, isMagnet).uuid;

  let accountPromise;
  if (!userContext.has_invitation_account) {
    // We manually create an invitations account since it's required to load the drafts & orders pages.
    accountPromise = creatInvitationsAccount().then(() => ({}));
  } else {
    accountPromise = Promise.resolve({});
  }

  return accountPromise
    .then(result => {
      if (
        !result.errors &&
        form &&
        form.customizations &&
        form.customizations.photo &&
        form.customizations.photo.valid &&
        form.customizations.pixelCrop
      ) {
        return photoUploadHandler(form.customizations.photo, form.customizations.pixelCrop).then(
          photoUUID => ({
            ...result,
            photoUUID,
          })
        );
      }

      return result;
    })
    .then(result => {
      if (result.errors) {
        return result;
      }

      const weddingNames = extractWeddingNames(form.weddingDetails);

      if (!weddingNames) {
        return { errors: { weddingDetails: 'Please make sure you enter your wedding details.' } };
      }

      const substitutions = createSubstitutions(form && form.weddingDetails);

      const cardInfo = {
        variation_uuid: activeVariationUUID,
        suite_uuid: suiteUUID,
        wedding_details: {
          ...weddingNames,
          event_date: !form.weddingDetails.stillDeciding && form.weddingDetails.date,
          ...substitutions,
        },
      };

      const body = {
        ...cardInfo,
        photo_uuid: result.photoUUID,
      };

      const cannotSaveDraft = 'Your single sample draft could not be saved.';

      return ApiService.post('/web-api/v1/card-single-sample/draft', body).then(saveDraftResult => {
        if (saveDraftResult.status === 'error' || saveDraftResult.errors) {
          return { errors: { saveDraft: saveDraftResult.message || cannotSaveDraft } };
        }

        return {};
      });
    });
}

export function getTemplate(
  cardSuiteDetails,
  variation,
  color,
  weddingDetails,
  sampleNotCustomizable,
  isMagnet
) {
  if (!cardSuiteDetails || !color) {
    return Promise.resolve();
  }

  const matched = variation || matchColorToCardVariation(cardSuiteDetails, color, isMagnet);

  const substitutions = sampleNotCustomizable ? {} : createSubstitutions(weddingDetails);

  return ApiService.post('/web-api/v1/card-template/preview/by-variations', {
    variation_uuids: [matched.uuid],
    substitutions,
  }).then(data => {
    return _values(data)[0];
  });
}

export function getLocationData(userContext) {
  const weddingDetails = extractUserWeddingDetails(userContext);
  const slug = weddingDetails && weddingDetails.slug;

  if (!slug) {
    return Promise.resolve(null);
  }

  return ApiService.get(`/web-api/v1/card-single-sample/prefill-content/${slug}`);
}



// WEBPACK FOOTER //
// ./src/cards/components/common/SingleSample/util/api.js